// OWNER: LG-SIGNALS. CONTRACT: signatures fixed (LEADGEN-SPEC §7.4 lint). Shared by the hook engine (leadgen/hooks.ts),
// CONTENT (content/validate.ts, beside NEVER_SAY) and AI-TRAINING (ai/brief.ts on openerLine). Browser-safe: no Worker types.
// OPENER_NEVER and OPENER_NEVER_WORDS are spec-verbatim values, live now so CONTENT and AI-TRAINING can import them today.
import type { HookUsage } from './api-leadgen';

export const OPENER_NEVER = /\bgoogle\b|\blisting\b|\bprofile\b|\bverif(y|ied|ication)\b|\bupdate\b|\b(un)?claim(ed)?\b|\bpermanently closed\b|\bsuspend/i;

/** The drawer's "Don't say" line (§11 block 2). */
export const OPENER_NEVER_WORDS: readonly string[] = ['Google', 'listing', 'profile', 'verify', 'update', 'claim', 'permanently closed', 'suspend'];

/** Same normalisation as ai/text.ts normalizeForMatch (kept local so this file stays browser-safe). */
function normalize(s: string): string {
  return s
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[‘’‚‛′´`]/g, "'")
    .replace(/[“”„‟″]/g, '"')
    .replace(/[‐-―−]/g, '-')
    .replace(/…/g, '...')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Numbers written with digits, as ai/text.ts reads them: "4.6", "5:30", "1,500" -> "1500". */
function numbersIn(s: string): string[] {
  return (normalize(s).match(/\d+(?:[.,:]\d+)*/g) ?? []).map((n) => n.replace(/,/g, ''));
}

/** Every number written with digits in `claim` also appears in `evidence` (same rule as ai/text.ts numbersSupported). */
export function numbersSupported(claim: string, evidence: string): boolean {
  const ev = new Set(numbersIn(evidence));
  return numbersIn(claim).every((n) => ev.has(n));
}

/** Facts the time words must match: "today" only if checked today; "the past year" ≤ 12 months; "a couple" = exactly 2;
 *  "a few" = 3-5. */
export interface TimeWordFacts { checkedToday: boolean; spanMonths: number | null; count: number | null }

const TODAY = /\btoday\b/i;
const PAST_YEAR = /\bpast\s+(?:year|12\s+months|twelve\s+months)\b/i;
const A_COUPLE = /\ba\s+couple\b/i;
const A_FEW = /\ba\s+few\b/i;

function timeWordProblems(text: string, facts: TimeWordFacts): string[] {
  const p: string[] = [];
  if (TODAY.test(text) && !facts.checkedToday) p.push('says "today" but the fact was not checked today');
  if (PAST_YEAR.test(text) && !(facts.spanMonths !== null && facts.spanMonths <= 12)) p.push('says "the past year" but the facts are not all from the last 12 months');
  if (A_COUPLE.test(text) && facts.count !== 2) p.push(`says "a couple" but the count is ${facts.count ?? 'unknown'} (must be exactly 2)`);
  if (A_FEW.test(text) && !(facts.count !== null && facts.count >= 3 && facts.count <= 5)) p.push(`says "a few" but the count is ${facts.count ?? 'unknown'} (must be 3 to 5)`);
  return p;
}

export function timeWordsOk(text: string, facts: TimeWordFacts): boolean {
  return timeWordProblems(text, facts).length === 0;
}

/** All three rules. `usage` 'opener' applies OPENER_NEVER; mid_call and meeting sentences may name the thing. */
export function lintOpener(text: string, ctx: { evidence: string; facts: TimeWordFacts; usage: HookUsage }): { ok: boolean; problems: string[] } {
  const problems: string[] = [];
  if (!text.trim()) problems.push('empty sentence');
  if (/[{}]/.test(text)) problems.push('has an unfilled slot');
  if (ctx.usage === 'opener') {
    const m = OPENER_NEVER.exec(text);
    if (m) problems.push(`opener says a banned word ("${m[0]}")`);
  }
  const evidenceNumbers = new Set(numbersIn(ctx.evidence));
  const missing = numbersIn(text).filter((n) => !evidenceNumbers.has(n));
  if (missing.length) problems.push(`has a number the evidence doesn't contain (${[...new Set(missing)].join(', ')})`);
  problems.push(...timeWordProblems(text, ctx.facts));
  return { ok: problems.length === 0, problems };
}
